import { profileFetch } from "../profileSession";
import { API_BASE, getToken, req } from "./transport";
import type { ProjectAgreement } from "./types";
import type { AgreementDocument } from "./types";

export const projectsApi = {
  // Increment 3: Projects & Bids
  async getProject(id: string) {
    const r = await req<{ item: any }>(`/marketplace/projects/${encodeURIComponent(id)}`);
    return r?.item ?? null;
  },

  async getBids(projectId: string) {
    return req<{
      isOwner: boolean; total: number; status: string; awardedBidId: string | null;
      biddingClosed: boolean; canEditBid: boolean;
      bids: any[]; myBid?: any | null;
    }>(`/projects/${encodeURIComponent(projectId)}/bids`);
  },

  async submitBid(projectId: string, payload: Record<string, unknown>) {
    return req<{ bid: any; total: number }>(`/projects/${encodeURIComponent(projectId)}/bids`, {
      method: "POST", body: JSON.stringify(payload),
    });
  },

  async uploadBidDocument(projectId: string, bidId: string, file: File) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/documents/upload?filename=${encodeURIComponent(file.name)}`, {
      method: "POST", credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { document: { id: string; filename: string; size: number } };
  },

  async downloadBidDocument(projectId: string, bidId: string, document: { id: string; filename: string }) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/documents/${encodeURIComponent(document.id)}/download`, {
      credentials: "include", headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error("Could not download this document.");
    const url = URL.createObjectURL(await res.blob());
    const anchor = window.document.createElement("a");
    anchor.href = url; anchor.download = document.filename;
    window.document.body.appendChild(anchor); anchor.click(); anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  async deleteBidDocument(projectId: string, bidId: string, documentId: string) {
    return req<{ ok: boolean }>(`/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/documents/${encodeURIComponent(documentId)}`, { method: "DELETE" });
  },

  async shortlistBid(projectId: string, bidId: string) {
    return req<{ bid: { id: string; status: string } }>(
      `/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/shortlist`,
      { method: "POST" },
    );
  },

  async previewProjectAward(projectId: string, bidId: string, opts: { milestones?: { label: string; amount: number }[]; scopeOfWork?: string; proposedCompletionDate?: string }) {
    return req<{ document: AgreementDocument | null }>(`/project-agreements/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/preview`, { method: "POST", body: JSON.stringify(opts) });
  },

  async awardBid(projectId: string, bidId: string, opts?: { milestones?: { label: string; amount: number }[]; scopeOfWork?: string; proposedCompletionDate?: string; expectedDocumentId?: string }) {
    const body: NonNullable<typeof opts> = {};
    if (opts?.milestones) body.milestones = opts.milestones;
    if (opts?.scopeOfWork) body.scopeOfWork = opts.scopeOfWork;
    if (opts?.proposedCompletionDate) body.proposedCompletionDate = opts.proposedCompletionDate;
    if (opts?.expectedDocumentId) body.expectedDocumentId = opts.expectedDocumentId;
    return req<{ agreement: ProjectAgreement }>(
      `/projects/${encodeURIComponent(projectId)}/bids/${encodeURIComponent(bidId)}/award`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  async myBids(signal?: AbortSignal) {
    return req<{ bids: any[] }>("/projects/mine/bids", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  // Increment 6: attachments (bytes proxied through the API → GCS/local) + Q&A
  async uploadAttachment(projectId: string, file: File) {
    const token = getToken();
    const url = `${API_BASE}/projects/${encodeURIComponent(projectId)}/attachments/upload?filename=${encodeURIComponent(file.name)}`;
    const res = await profileFetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `Upload failed (${res.status})`);
    return data as { attachment: any };
  },

  async listAttachments(projectId: string) {
    return req<{ attachments: any[]; total: number }>(`/projects/${encodeURIComponent(projectId)}/attachments`);
  },

  async deleteAttachment(projectId: string, attId: string) {
    return req<{ ok: boolean }>(`/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attId)}`, { method: "DELETE" });
  },

  attachmentDownloadUrl(projectId: string, attId: string) {
    return `${API_BASE}/projects/${encodeURIComponent(projectId)}/attachments/${encodeURIComponent(attId)}/download`;
  },

  async listQuestions(projectId: string) {
    return req<{ messages: { id: string; senderUserId: number; senderName: string; message: string; createdAt: string }[]; total: number; isClosed: boolean }>(`/projects/${encodeURIComponent(projectId)}/questions`);
  },

  async postQuestionMessage(projectId: string, message: string) {
    return req<{ message: { id: string; senderUserId: number; senderName: string; message: string; createdAt: string } }>(`/projects/${encodeURIComponent(projectId)}/questions`, { method: "POST", body: JSON.stringify({ message }) });
  }
};
